/**
 * Mobile mirror sync layer
 *
 * The desktop pricing table is the canonical source of truth. Mobile cards
 * (rendered server-side from snippets/mobile-product-card.liquid) carry a
 * second set of `.mobile-size-cell__qty` inputs whose only job is to mirror
 * a canonical `.variant_qty_input_directship` identified by
 * data-source-input-id="qty-{variantId}-{warehouseCode}".
 *
 * Direction: mirror → canonical on user input/change/blur. canonical →
 * mirror after pre-population, after baseline reset, and whenever
 * canonical-side validation clamps or rounds a value.
 *
 * Why dispatch DOM events instead of jQuery .trigger():
 *   jQuery synthesizes its own event objects; native addEventListener
 *   handlers don't fire from .trigger(). Cart pre-population, dirty
 *   tracking, and the canonical input handler all use jQuery delegated
 *   listeners (which DO fire from real DOM events), so dispatching
 *   bubbling Events covers both worlds without coupling.
 *
 * Why ID lookup instead of selector:
 *   data-source-input-id avoids CSS escaping issues on generated IDs that
 *   may include hyphens, digits, or selector-significant characters.
 *
 * Visibility: mobile cards reflect the canonical row's filter classes via a
 * MutationObserver on the table. No filter logic is duplicated — the cards
 * just follow whatever the desktop layer already decided.
 */

import { normalizeAndValidateQuantityInput } from "../handlers/inputValidation.js";

let isSyncingFromMirror = false;
let visibilitySyncScheduled = false;

function normalizeMobileQty(value: string): string {
  return value.replace(/\D/g, "");
}

/**
 * Resolve the canonical input for a given mirror input via ID lookup.
 * Returns null when the canonical isn't in the DOM yet (e.g., a mirror
 * card was lazy-rendered before its row, or the source-input-id was
 * malformed). Caller short-circuits in that case.
 */
function getCanonicalForMirror(
  mirror: HTMLInputElement,
): HTMLInputElement | null {
  const sourceId = mirror.dataset.sourceInputId;
  if (!sourceId) return null;
  const source = document.getElementById(sourceId);
  return source instanceof HTMLInputElement ? source : null;
}

function getPositiveQuantity(input: HTMLInputElement): number {
  const quantity = parseInt(input.value, 10) || 0;
  return quantity > 0 ? quantity : 0;
}

function updateMirrorHighlight(mirror: HTMLInputElement): void {
  const hasQty = getPositiveQuantity(mirror) > 0;
  mirror.classList.toggle("has-qty", hasQty);
  mirror
    .closest(".mobile-size-cell")
    ?.classList.toggle("mobile-size-cell--has-qty", hasQty);
}

function updateMirrorDisplay(mirror: HTMLInputElement): void {
  updateMirrorHighlight(mirror);
  // Per-card subtotal markup was removed in v4 — full-cart totals now live in
  // the in-card Update Cart button, which mobileCardSubmit.ts refreshes on
  // its own input/change listeners.
}

function dispatchCanonicalInputEvents(source: HTMLInputElement): void {
  source.dispatchEvent(new Event("input", { bubbles: true }));
  source.dispatchEvent(new Event("change", { bubbles: true }));
}

/**
 * Sync a single mirror's value into its canonical input and dispatch the
 * native DOM events the canonical's listeners (jQuery-delegated and
 * otherwise) expect.
 *
 * The isSyncingFromMirror flag prevents the canonical→mirror handler below
 * from bouncing the new value right back into the mirror.
 */
function syncMirrorToCanonical(mirror: HTMLInputElement): void {
  const source = getCanonicalForMirror(mirror);
  if (!source) return;

  const cleaned = normalizeMobileQty(mirror.value);
  if (mirror.value !== cleaned) {
    mirror.value = cleaned;
  }

  if (source.value === cleaned) {
    updateMirrorDisplay(mirror);
    return;
  }

  isSyncingFromMirror = true;
  try {
    source.value = cleaned;
    dispatchCanonicalInputEvents(source);
  } finally {
    isSyncingFromMirror = false;
  }
  updateMirrorDisplay(mirror);
}

/**
 * Mobile blur path. Reuses the canonical blur logic
 * (normalizeAndValidateQuantityInput) so case-pack rounding and inventory
 * clamps run before any sticky-bar submission can fire. Final value is
 * copied back to the mirror so the UI reflects the clamped quantity.
 */
function handleMobileQtyBlur(mirror: HTMLInputElement): void {
  const source = getCanonicalForMirror(mirror);
  if (!source) return;

  isSyncingFromMirror = true;
  try {
    source.value = normalizeMobileQty(mirror.value);
    source.dispatchEvent(new Event("input", { bubbles: true }));
    source.dispatchEvent(new Event("change", { bubbles: true }));
  } finally {
    isSyncingFromMirror = false;
  }

  normalizeAndValidateQuantityInput(source);
  mirror.value = source.value;
  updateMirrorDisplay(mirror);
  dispatchCanonicalInputEvents(source);
}

/**
 * Reassign tabindex on every mobile mirror input so tab order is column-major
 * within each card AND each card occupies a distinct tabindex block.
 *
 * Liquid emits initial tabindexes based on `product_index`, but lazy-loaded
 * cards always render with `product_index=0` (the section rendering API has
 * no way to learn the family position), so without this reassignment all
 * lazy cards collide on the same tabindex range and the browser falls back
 * to DOM order — which makes tab jump between cards instead of staying
 * within one. We reassign from DOM order at JS time, ignoring whatever the
 * Liquid layer wrote.
 *
 * Per-card layout: card_base + col_index * num_sizes + row_index + 1.
 */
export function reassignMobileTabindexes(): void {
  const cards = Array.from(
    document.querySelectorAll<HTMLElement>(".mobile-inventory-product"),
  );
  cards.forEach((card, cardIndex) => {
    const rows = Array.from(
      card.querySelectorAll<HTMLElement>("tbody tr.mit-row"),
    );
    if (rows.length === 0) return;

    const numSizes = rows.length;
    const cardBase = (cardIndex + 1) * 1000;

    rows.forEach((row, rowIndex) => {
      const cells = Array.from(
        row.querySelectorAll<HTMLElement>(".mit-qty-cell"),
      );
      cells.forEach((cell, colIndex) => {
        const input = cell.querySelector<HTMLInputElement>(
          ".mobile-size-cell__qty",
        );
        if (!input) return;
        input.tabIndex = cardBase + colIndex * numSizes + rowIndex + 1;
      });
    });
  });
}

/**
 * Push every canonical input value into its matching mirror(s). Called once
 * after cart pre-population resolves, again after baseline snapshots, and
 * again after each lazy-row insert so newly rendered mobile cards inherit
 * any cart values their canonical row already carries.
 */
export function syncAllMirrorsFromCanonical(): void {
  document
    .querySelectorAll<HTMLInputElement>(".mobile-size-cell__qty")
    .forEach((mirror) => {
      const source = getCanonicalForMirror(mirror);
      if (!source) return;
      if (mirror.value !== source.value) {
        mirror.value = source.value;
      }
      updateMirrorDisplay(mirror);
    });
}

/**
 * Walk every mobile card and reconcile its visibility with the matching
 * canonical rows. v4: warehouses are columns inside one transposed table, so
 * there are no per-warehouse subsections to walk — the card is the unit of
 * visibility.
 *
 * Mirror also copies the desktop's `show-this-color` class onto the card so
 * CSS can gate the in-card Update Cart button to only the active color.
 *
 * No filter logic is computed here — we just read whatever filter classes
 * the existing desktop modules already set on .warehouse_row /
 * .inventory_master_table. Availability filtering is hidden on mobile (Q-11)
 * so we ignore `show-this-warehouse` when computing card visibility.
 */
function syncMobileCardVisibility(): void {
  const table = document.querySelector<HTMLElement>(".inventory_master_table");
  const filteringActive =
    table?.classList.contains("filtering-active") ?? false;

  document
    .querySelectorAll<HTMLElement>(".mobile-inventory-product")
    .forEach((card) => {
      const handle = card.dataset.productHandle;
      if (!handle) return;

      // Find any backing canonical row for this color. Without one, the
      // card has no inputs to mirror — hide it.
      const $rows = $(`.warehouse_row[data-product-handle="${handle}"]`);
      if ($rows.length === 0) {
        card.classList.add("mobile-inventory-product--hidden");
        card.classList.remove("show-this-color");
        return;
      }

      // When color filtering is active, the card is visible iff at least one
      // of its canonical rows carries .show-this-color. When not filtering,
      // every card is visible.
      const showThisColor =
        !filteringActive ||
        $rows.is(".show-this-color");

      card.classList.toggle(
        "mobile-inventory-product--hidden",
        !showThisColor,
      );
      // Mirror the .show-this-color class onto the card so CSS can gate the
      // in-card Update Cart button to the active color (single-color mode
      // ensures exactly one color carries the class at a time).
      card.classList.toggle("show-this-color", showThisColor);
    });
}

function scheduleVisibilitySync(): void {
  if (visibilitySyncScheduled) return;
  visibilitySyncScheduled = true;
  queueMicrotask(() => {
    visibilitySyncScheduled = false;
    syncMobileCardVisibility();
  });
}

/**
 * Watch the canonical table for filter class changes on itself and any of
 * its descendant rows. The cost on desktop is bounded — the observer fires
 * only on attribute changes, and syncMobileCardVisibility iterates a small
 * number of cards.
 */
function initMobileCardVisibilityObserver(): void {
  const table = document.querySelector(".inventory_master_table");
  if (!table) return;

  const observer = new MutationObserver(() => {
    scheduleVisibilitySync();
  });

  observer.observe(table, {
    attributes: true,
    attributeFilter: ["class"],
    subtree: true,
  });
}

/**
 * Re-render mobile cards for a specific handle when its rows are
 * lazy-loaded. Currently a no-op for *existing* server-rendered cards
 * (they're already in the DOM); included as the listener entry point so a
 * future change that defers card render until lazy time can hook the same
 * event.
 *
 * The actual lazy mobile-card insertion is handled by lazyProductLoader.ts
 * (it requests both sections in one call). After insertion we just re-sync
 * values and visibility.
 */
function handleLazyRowInserted(event: Event): void {
  const detail = (event as CustomEvent<{ handle: string }>).detail;
  if (!detail?.handle) return;
  syncAllMirrorsFromCanonical();
  scheduleVisibilitySync();
}

export function initMobileMirror(): void {
  // Live mirror → canonical sync as the user types.
  $(document).on(
    "input",
    ".mobile-size-cell__qty",
    function (this: HTMLInputElement) {
      syncMirrorToCanonical(this);
    },
  );

  // Mirror change (committed value, e.g., after paste): sync.
  $(document).on(
    "change",
    ".mobile-size-cell__qty",
    function (this: HTMLInputElement) {
      syncMirrorToCanonical(this);
    },
  );

  // Mirror blur → canonical normalization (clamp, case-pack), then mirror back.
  $(document).on(
    "blur",
    ".mobile-size-cell__qty",
    function (this: HTMLInputElement) {
      handleMobileQtyBlur(this);
    },
  );

  // Canonical → mirror sync when canonical changes from non-mirror sources
  // (cart pre-population dispatches no events, but normalizeAndValidate may
  // mutate value programmatically; defensive coverage either way).
  $(document).on(
    "input change",
    ".variant_qty_input_directship",
    function (this: HTMLInputElement) {
      if (isSyncingFromMirror) return;
      const sourceId = this.id;
      if (!sourceId) return;
      document
        .querySelectorAll<HTMLInputElement>(
          ".mobile-size-cell__qty",
        )
        .forEach((mirror) => {
          if (mirror.dataset.sourceInputId !== sourceId) return;
          if (mirror.value !== this.value) mirror.value = this.value;
          updateMirrorDisplay(mirror);
        });
    },
  );

  document.addEventListener("product:lazy-row-inserted", handleLazyRowInserted);

  initMobileCardVisibilityObserver();
  // Initial pass for server-rendered cards.
  syncMobileCardVisibility();
}
